import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';

interface TenantProfile {
  readonly tenantId: string;
  readonly tenantCode: string;
  readonly tenantName: string;
  readonly tenantStatus: string;
  readonly companyCode: string | null;
  readonly companyName: string | null;
  readonly legalName: string | null;
  readonly panNumber: string | null;
  readonly country: string | null;
  readonly companyEmail: string | null;
  readonly userCount: number;
  readonly adminLoginId: string | null;
  readonly adminEmail: string | null;
  readonly adminMobile: string | null;
  readonly adminName: string | null;
}

// t014_user_master enforces ^\+[1-9]\d{6,14}$, so the dial code is supplied
// here and the user types only the national number.
const DIAL_CODE = '+91';

// The server returns the policy's own rule names; show them as sentences.
const PASSWORD_RULE_KEYS: Record<string, string> = {
  PWD_TOO_SHORT: 'm19.tenantEdit.admin.pwd.tooShort',
  PWD_TOO_LONG: 'm19.tenantEdit.admin.pwd.tooLong',
  PWD_REQUIRES_UPPERCASE: 'm19.tenantEdit.admin.pwd.upper',
  PWD_REQUIRES_LOWERCASE: 'm19.tenantEdit.admin.pwd.lower',
  PWD_REQUIRES_NUMERIC: 'm19.tenantEdit.admin.pwd.numeric',
  PWD_REQUIRES_SPECIAL: 'm19.tenantEdit.admin.pwd.special',
};

/**
 * Platform Edit screen for one Tenant.
 *
 * Tenant code and company code are shown read-only: tenant code is what every
 * user sends at sign-in, so changing it would lock that tenant out instantly.
 * The Tenant Admin login id is read-only once it exists; before that, this
 * screen is where it is created.
 */
export function M19TenantEditPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const { code } = useParams<{ code: string }>();

  const [data, setData] = useState<TenantProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [tenantName, setTenantName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [country, setCountry] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');

  const [loginSuffix, setLoginSuffix] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminMobile, setAdminMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [creating, setCreating] = useState(false);

  const load = (): void => {
    apiRequest<{ data: TenantProfile }>(`/v1/platform/tenants/${code}/profile`, { token })
      .then((r) => {
        setData(r.data);
        setTenantName(r.data.tenantName ?? '');
        setCompanyName(r.data.companyName ?? '');
        setLegalName(r.data.legalName ?? '');
        setPanNumber(r.data.panNumber ?? '');
        setCountry(r.data.country ?? '');
        setCompanyEmail(r.data.companyEmail ?? '');
      })
      .catch((e) => setError((e as Error).message));
  };

  const readableError = (raw: string): string => {
    const hit = Object.keys(PASSWORD_RULE_KEYS).filter((c) => raw.includes(c));
    if (!hit.length) return raw;
    return `${tr('m19.tenantEdit.admin.pwd.rejected')} ${hit.map((c) => tr(PASSWORD_RULE_KEYS[c])).join(' ')}`;
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token, code]);

  const onSave = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/platform/tenants/${code}/profile`, {
        method: 'PUT', token,
        body: {
          tenantName,
          companyName,
          legalName,
          panNumber: panNumber.trim() || null,
          country: country.trim().toUpperCase(),
          companyEmail: companyEmail.trim() || null,
        },
      });
      setNotice(tr('m19.tenantEdit.saved'));
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onCreateAdmin = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (password !== confirmPassword) {
      setError(tr('m19.tenantEdit.admin.passwordMismatch'));
      return;
    }
    if (adminMobile.replace(/\D/g, '').length !== 10) {
      setError(tr('m19.tenantEdit.admin.mobileInvalid'));
      return;
    }
    setCreating(true);
    try {
      await apiRequest(`/v1/platform/tenants/${code}/admin`, {
        method: 'POST', token,
        body: {
          loginIdSuffix: loginSuffix.trim(),
          firstName,
          lastName,
          email: adminEmail.trim(),
          mobile: `${DIAL_CODE}${adminMobile.replace(/\D/g, '')}`,
          password,
        },
      });
      setPassword('');
      setConfirmPassword('');
      setNotice(tr('m19.tenantEdit.admin.created'));
      load();
    } catch (err) {
      setError(readableError((err as Error).message));
    } finally {
      setCreating(false);
    }
  };

  if (error && !data) return <p role="alert">{error}</p>;
  if (!data) return <p>{tr('m19.tenantEdit.loading')}</p>;

  return (
    <main aria-label="tenant-edit">
      <h1>{tr('m19.tenantEdit.title', { code: data.tenantCode })}</h1>
      <Link to="/admin/platform/tenants">{tr('m19.tenantEdit.back')}</Link>

      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}

      <form onSubmit={onSave} aria-label="tenant-profile-form">
        <h2>{tr('m19.tenantEdit.section.profile')}</h2>

        <p>{tr('m19.tenantEdit.labelTenantCode')}: <strong aria-label="tenant-code-readonly">{data.tenantCode}</strong></p>
        <p>{tr('m19.tenantEdit.labelCompanyCode')}: <strong aria-label="company-code-readonly">{data.companyCode ?? '—'}</strong></p>

        <div className="tl-field-row">
          <label htmlFor="te-tenant-name">{tr('m19.tenantEdit.labelTenantName')}</label>
          <input id="te-tenant-name" value={tenantName} onChange={(e) => setTenantName(e.target.value)} required />
        </div>

        <div className="tl-field-row">
          <label htmlFor="te-company-name">{tr('m19.tenantEdit.labelCompanyName')}</label>
          <input id="te-company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
        </div>

        <div className="tl-field-row">
          <label htmlFor="te-legal-name">{tr('m19.tenantEdit.labelLegalName')}</label>
          <input id="te-legal-name" value={legalName} onChange={(e) => setLegalName(e.target.value)} required />
        </div>

        <div className="tl-field-row">
          <label htmlFor="te-email">{tr('m19.tenantEdit.labelEmail')}</label>
          <input id="te-email" type="email" value={companyEmail} onChange={(e) => setCompanyEmail(e.target.value)} />
        </div>

        {/* No GSTIN here: a company registers for GST per state, so its
            registrations are captured on the company GST configuration screen. */}
        <div className="tl-field-row">
          <label htmlFor="te-pan">{tr('m19.tenantEdit.labelPan')}</label>
          <input id="te-pan" value={panNumber} onChange={(e) => setPanNumber(e.target.value)} />
        </div>

        <div className="tl-field-row">
          <label htmlFor="te-country">{tr('m19.tenantEdit.labelCountry')}</label>
          <input id="te-country" value={country} onChange={(e) => setCountry(e.target.value)} maxLength={2} required />
        </div>

        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? tr('m19.tenantEdit.saving') : tr('m19.tenantEdit.save')}
        </Button>
      </form>

      <section aria-label="tenant-admin-login">
        <h2>{tr('m19.tenantEdit.section.admin')}</h2>

        {data.adminLoginId ? (
          <>
            <p>{tr('m19.tenantEdit.labelLoginId')}: <strong aria-label="login-id-readonly">{data.adminLoginId}</strong></p>
            <p>{tr('m19.tenantEdit.admin.name')}: {data.adminName ?? '—'}</p>
            <p>{tr('m19.tenantEdit.admin.email')}: {data.adminEmail ?? '—'}</p>
            <p>{tr('m19.tenantEdit.admin.mobile')}: {data.adminMobile ?? '—'}</p>
            <p>{tr('m19.tenantEdit.admin.readOnlyNote')}</p>
          </>
        ) : (
          <form onSubmit={onCreateAdmin} aria-label="create-tenant-admin-form">
            <p>{tr('m19.tenantEdit.admin.none')}</p>

            {/* The prefix is derived from the tenant, never typed, so a login id
                can never claim a tenant its account does not belong to. */}
            <div className="tl-field-row">
              <label htmlFor="te-login-suffix">{tr('m19.tenantEdit.admin.loginId')}</label>
              <span aria-label="login-id-prefix"><strong>{data.tenantCode}_</strong></span>
              <input
                id="te-login-suffix"
                value={loginSuffix}
                onChange={(e) => setLoginSuffix(e.target.value)}
                pattern="[A-Za-z0-9]{2,20}"
                title={tr('m19.tenantEdit.admin.loginIdHint')}
                required
              />
              <small>
                {tr('m19.tenantEdit.admin.loginIdHint')}
                {loginSuffix ? ` ${tr('m19.tenantEdit.admin.loginIdPreview', { id: `${data.tenantCode}_${loginSuffix}` })}` : ''}
              </small>
            </div>

            <div className="tl-field-row">
              <label htmlFor="te-first-name">{tr('m19.tenantEdit.admin.firstName')}</label>
              <input id="te-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </div>

            <div className="tl-field-row">
              <label htmlFor="te-last-name">{tr('m19.tenantEdit.admin.lastName')}</label>
              <input id="te-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </div>

            <div className="tl-field-row">
              <label htmlFor="te-admin-email">{tr('m19.tenantEdit.admin.email')}</label>
              <input id="te-admin-email" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} required />
            </div>

            <div className="tl-field-row">
              <label htmlFor="te-admin-mobile">{tr('m19.tenantEdit.admin.mobile')}</label>
              <span aria-label="mobile-dial-code">{DIAL_CODE}</span>
              <input
                id="te-admin-mobile"
                value={adminMobile}
                onChange={(e) => setAdminMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                inputMode="numeric"
                pattern="[0-9]{10}"
                maxLength={10}
                placeholder="9876543210"
                required
              />
              <small>{tr('m19.tenantEdit.admin.mobileHint')}</small>
            </div>

            <div className="tl-field-row">
              <label htmlFor="te-password">{tr('m19.tenantEdit.admin.password')}</label>
              <input id="te-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required />
              <small>{tr('m19.tenantEdit.admin.pwd.rules')}</small>
            </div>

            <div className="tl-field-row">
              <label htmlFor="te-password2">{tr('m19.tenantEdit.admin.confirmPassword')}</label>
              <input id="te-password2" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" required />
            </div>

            <p>{tr('m19.tenantEdit.admin.tempPasswordNote')}</p>

            <Button type="submit" variant="primary" disabled={creating}>
              {creating ? tr('m19.tenantEdit.admin.creating') : tr('m19.tenantEdit.admin.create')}
            </Button>
          </form>
        )}
      </section>
    </main>
  );
}
